import {
    DriveItem,
    FileAttachment,
    Message,
} from "@microsoft/microsoft-graph-types";
import { EventBus } from "../../contexts";
import { inheritHttpStatus, logger, LOGS } from "../../utils";
import { extractLinks } from "../../utils/extractLinks/extractLinks";
import { isSearchResultMatchKeyWords } from "../../utils/searchResultMatchKeywords/searchResultMatchKeywords";
import { generateUUID } from "../../utils/uuid/generateUUID";
import {
    AgendaFilter,
    CreateAgendaItemData,
    UpdateAgendaItemData,
} from "../agenda";
import { ViewFilter } from "../customView";
import {
    Attachment,
    EmailSearchFilter,
    Mail,
    MailCopyRef,
    MailSend,
    NavItem,
    ReplyForward,
} from "../mail";
import { MailFolder } from "./graph/types/graph";
import {
    AgendaEventsPage,
    AgendaItemBody,
    BatchDeleteItem,
    BatchDeleteResult,
    BatchReadStatusItem,
    BatchReadStatusResult,
    ContactPerson,
    DataSearchResult,
    IDataApi,
    SendMailResult,
    SentMailsCriteria,
} from "./IDataApi";

export class DataManager {
    // #region Properties

    protected eventBus: EventBus;

    private dataApi: IDataApi;

    // #endregion Properties

    constructor(eventBus: EventBus, dataApi: IDataApi) {
        logger.info(LOGS.DataManager, "()");

        this.eventBus = eventBus;
        this.dataApi = dataApi;
    }

    // #region Initialisation and cleanup

    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the GraphManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.DataManager,
            "DataManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.DataManager,
                "  Phase 1: Stopping active processes",
            );

            // Cancel pending Graph user profile fetch if in progress
            // if (this.fetchingGraphUserProfile) {
            //     // Note: Can't actually cancel the Promise, but we'll reset the reference
            // }
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `DataManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        // Phase 2: Clear internal state and caches
        try {
            logger.info(LOGS.DataManager, "  Phase 2: Clearing internal state");
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `DataManager.cleanup() - Error in Phase 2: ${error}`,
            );
        }

        // Phase 3: Nullify manager references
        try {
            logger.info(
                LOGS.DataManager,
                "  Phase 3: Nullifying manager references",
            );

            // Set dataApi reference to undefined
            this.dataApi = undefined as any;
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `DataManager.cleanup() - Error in Phase 3: ${error}`,
            );
        }

        logger.info(
            LOGS.DataManager,
            "DataManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Event Handlers

    private _registerEvents() {}

    // #endregion Event Handlers

    // #region Public Methods

    // #region Mails

    /**
     *
     * @param filter - Filter object to search for emails
     * @returns Array of Tuples {id: string; internetMessageId: string}
     */
    public async searchForNewMails(
        filter: EmailSearchFilter,
    ): Promise<{ id: string; internetMessageId: string }[]> {
        // TODO - Remplacer cette fonction par un appel à searchMailsFromUpdatedViewFilter si possible
        logger.info(
            [LOGS.DataManager],
            `searchForNewMails(${JSON.stringify(filter)})`,
        );

        const startTime = performance.now();
        logger.info([LOGS.Perfs], `!!! searchMail with filter...`);

        try {
            const response = await this.dataApi.searchForNewMails(filter);
            logger.info(
                LOGS.DataManager,
                `!!! searchForNewMails response`,
                response,
            );
            logger.info(
                LOGS.Perfs,
                `    !!!  callEndPoint                   : ${(performance.now() - startTime).toFixed(2)}ms`,
            );

            return response;
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error in searchForNewMails: ${error}`,
            );
            if (error.details) {
                logger.info(
                    [LOGS.DataManager],
                    "searchForNewMailsError",
                    error.details,
                );
            }
            throw error;
        }
    }

    public async getMailsParentFolderIds(
        internetMessageIds: string[],
    ): Promise<Record<string, string>> {
        logger.info(
            [LOGS.DataManager],
            `getMailsParentFolderIds ${internetMessageIds.length}`,
        );

        const parentFolderIdResponse =
            await this.dataApi.getMailsParentFolderIds(internetMessageIds);

        return parentFolderIdResponse;
    }

    public async getMailById(mailId: string): Promise<Mail> {
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `Getting mail by ID...`,
        );

        const response = await this.dataApi.getMailById(mailId);

        const foundLinks = extractLinks(response.uniqueBody);

        response.links = foundLinks;

        return response;
    }

    public async getMailsByIds(
        mailIds: string[],
    ): Promise<{ mails: Mail[]; failedIds: string[] }> {
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `Getting ${mailIds.length} mails by ID (batched)...`,
        );

        const { mails, failedIds } = await this.dataApi.getMailsByIds(mailIds);

        // Parité avec getMailById : extraction des liens depuis uniqueBody pour chaque copie.
        for (const mail of mails) {
            mail.links = extractLinks(mail.uniqueBody);
        }

        return { mails, failedIds };
    }

    public async getMailAttachments(
        mailId: string,
        parentFolderId?: string,
    ): Promise<FileAttachment[]> {
        logger.info(
            LOGS.DataManager,
            `log getMailAttachments() for mail ${mailId}`,
        );

        try {
            const attachmentsResult = await this.dataApi.getMailAttachments(
                mailId,
                parentFolderId,
            );
            return attachmentsResult;
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error fetching attachments for mail ${mailId}: ${error}`,
            );
            throw error; // Relance l'erreur pour qu'elle soit gérée par l'appelant
        }
    }

    public async deleteMail(mailId: string): Promise<void> {
        try {
            await this.dataApi.deleteMail(mailId);
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error deleting mail ${mailId}: ${error}`,
            );
            // AB#1764 — préserver le status HTTP (404 → suppression idempotente en amont).
            throw inheritHttpStatus(
                new Error(`Error deleting mail ${mailId}: ${error}`),
                error,
            );
        }
    }

    /**
     * Supprime un lot d'emails via le provider actif (Graph ou Google)
     * ════════════════════════════════════════════════════════════════════
     *
     * Délègue à `dataApi.batchDeleteMails`. En cas d'erreur totale (exception
     * non gérée par le provider), retourne un résultat avec tous les items
     * dans `failed` sans relancer l'exception, afin de ne jamais bloquer l'appelant.
     *
     * @param items       - Liste des items à supprimer (messageId + isPermanent)
     * @param maxRetries  - Transmis au provider (ignoré par GoogleManager)
     * @returns           - `permanentDeleted` / `moved` / `failed` en Graph IDs
     */
    public async batchDeleteMails(
        items: BatchDeleteItem[],
        maxRetries?: number,
    ): Promise<BatchDeleteResult> {
        logger.info(
            [LOGS.DataManager],
            `batchDeleteMails: ${items.length} item(s)`,
        );
        try {
            return await this.dataApi.batchDeleteMails(items, maxRetries);
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error in batchDeleteMails: ${error}`,
            );
            return {
                permanentDeleted: [],
                moved: [],
                notFound: [],
                failed: items.map((i) => i.messageId),
            };
        }
    }

    /**
     * Fetch mails with pagination support
     * ═══════════════════════════════════════════════════════════
     *
     * @param options                       - Configuration options
     * @param options.skip                  - Number of items to skip (for pagination)
     * @param options.top                   - Maximum number of items to return
     * @returns                             - MailCopyRef[] (une réf de copie par item du dossier : internetMessageId + id + parentFolderId)
     */
    public async fetchGeneralInternetMessageIds(
        mailFolder: MailFolder = MailFolder.Inbox,
        top: number = 30,
        skip: number = 0,
    ): Promise<MailCopyRef[]> {
        // const startTime = performance.now();
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `fetchMails (skip: ${skip}, top: ${top}) ... `,
        );

        try {
            const result = await this.dataApi.fetchGeneralInternetMessageIds(
                mailFolder,
                top,
                skip,
            );
            return result;
        } catch (error) {
            logger.error(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                `Error in fetchGeneralInternetMessageIds: ${error}`,
            );
            throw error;
        }
    }

    /**
     * Les copies du dossier Envoyés qui répondent aux critères (boîte « envoyes » de l'assistant),
     * `limit` au plus, triées par date d'envoi. Une lecture impossible lève : jamais une liste vide.
     */
    public async searchSentMailCopyRefs(
        criteria: SentMailsCriteria,
        limit: number,
    ): Promise<MailCopyRef[]> {
        logger.info([LOGS.DataManager], `searchSentMailCopyRefs ...`);
        try {
            return await this.dataApi.searchSentMailCopyRefs(criteria, limit);
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error in searchSentMailCopyRefs: ${error}`,
            );
            throw error;
        }
    }

    /**
     * #4 — Émission CENTRALISÉE de l'event 'mail-sent'. Le provider renvoie de quoi émettre :
     *  - `mailSentId` : id connu immédiatement (Google) → émis tout de suite ;
     *  - `deferredMailSentId` : promesse de l'id final (Graph : copie Sent re-résolue en arrière-plan)
     *     → émis quand elle se résout, SANS bloquer le retour de l'envoi.
     */
    private _emitMailSent(result: SendMailResult): void {
        if (result.mailSentId !== null) {
            this.eventBus.emit("mail-sent", result.mailSentId);
        }
        if (result.deferredMailSentId) {
            void result.deferredMailSentId
                .then((id) => this.eventBus.emit("mail-sent", id))
                .catch((error) =>
                    logger.error(
                        [LOGS.DataManager],
                        `Émission différée de 'mail-sent' échouée: ${error}`,
                    ),
                );
        }
    }

    public async emitMailSentByInternetMessageId(
        internetMessageId: string,
    ): Promise<void> {
        if (!this.dataApi) {
            throw new Error("DataApi is not set");
        }

        this._emitMailSent({
            mailSentId: null,
            deferredMailSentId: this.dataApi
                .resolveSentMailId(internetMessageId)
                .then((id) => {
                    if (!id) {
                        throw new Error(
                            `Copie envoyee introuvable pour ${internetMessageId}`,
                        );
                    }

                    return id;
                }),
        });
    }

    public async sendMail(mailData: MailSend): Promise<boolean> {
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `sendMail to ${mailData.toRecipients.join(", ")}...`,
        );

        try {
            // Process inline images before sending
            const processedMailData = this._processInlineImages(
                mailData,
            ) as MailSend;

            this._emitMailSent(await this.dataApi.sendMail(processedMailData));
            logger.info([LOGS.DataManager], "  Email sent successfully");

            return true;
        } catch (error) {
            logger.error(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                `  !!! Error in sendMail: ${error}`,
            );
            return false;
        }
    }

    public async replyToMail(
        messageId: string,
        replyData: ReplyForward,
        replyAll: boolean = false,
    ): Promise<boolean> {
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `log reply to message ${messageId}...`,
        );

        try {
            // Process inline images before sending
            const processedReplyData = this._processInlineImages(
                replyData,
            ) as ReplyForward;

            this._emitMailSent(
                await this.dataApi.replyToMail(
                    messageId,
                    processedReplyData,
                    replyAll,
                ),
            );
            logger.info([LOGS.DataManager], "  Reply sent successfully");

            return true;
        } catch (error) {
            logger.error(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                `  !!! Error in replyToMail: ${error}`,
            );
            return false;
        }
    }

    public async setReadStatus(
        messageId: string,
        isRead: boolean,
    ): Promise<boolean> {
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `Setting read status to ${isRead} for message ${messageId}...`,
        );

        try {
            const response = await this.dataApi.setReadStatus(
                messageId,
                isRead,
            );
            logger.info(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                "Read status updated successfully",
            );

            return response;
        } catch (error) {
            logger.error(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                `Error in setReadStatus: ${error}`,
            );
            return false;
        }
    }

    /**
     * Marque un lot d'emails comme lus/non-lus via le provider actif (Graph ou Google)
     * ════════════════════════════════════════════════════════════════════════════════════
     *
     * Délègue à `dataApi.batchSetReadStatus`. En cas d'erreur totale (exception
     * non gérée par le provider), retourne tous les IDs dans `failed`
     * sans relancer l'exception, afin de ne jamais bloquer l'appelant.
     *
     * @param items       - Liste des items { messageId, isRead }
     * @param maxRetries  - Transmis au provider (ignoré par GoogleManager)
     * @returns           - `success` / `failed` en Graph IDs
     */
    public async batchSetReadStatus(
        items: BatchReadStatusItem[],
        maxRetries?: number,
    ): Promise<BatchReadStatusResult> {
        logger.info(
            [LOGS.DataManager],
            `batchSetReadStatus: ${items.length} item(s)`,
        );
        try {
            return await this.dataApi.batchSetReadStatus(items, maxRetries);
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error in batchSetReadStatus: ${error}`,
            );
            return { success: [], failed: items.map((i) => i.messageId) };
        }
    }

    public async setImportance(
        messageId: string,
        importance: "low" | "normal" | "high",
    ): Promise<boolean> {
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `Setting importance to ${importance} for message ${messageId}...`,
        );

        try {
            const response = await this.dataApi.setImportance(
                messageId,
                importance,
            );
            logger.info(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                "Importance updated successfully",
            );
            return response;
        } catch (error) {
            logger.error(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                `Error in setImportance: ${error}`,
            );
            return false;
        }
    }

    public async setFlag(
        messageId: string,
        flagStatus: "notFlagged" | "complete" | "flagged",
    ): Promise<boolean> {
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `Setting flag status to ${flagStatus} for message ${messageId}...`,
        );

        try {
            const flagBody =
                flagStatus === "notFlagged"
                    ? null
                    : {
                          flagStatus: flagStatus,
                      };

            await this.dataApi.setFlag(messageId, flagBody);
            logger.info(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                "Flag status updated successfully",
            );
            return true;
        } catch (error) {
            logger.error(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                `Error in setFlag: ${error}`,
            );
            return false;
        }
    }

    public async searchContact(
        searchString: string,
        top: number = 20,
    ): Promise<string[]> {
        logger.info([LOGS.DataManager], `searchContact for ${searchString}...`);

        let response: string[] = [];

        try {
            response = await this.dataApi.searchContact(searchString, top);
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error in searchContact call to dataApi: ${error}`,
            );
            throw error;
        }

        logger.info([LOGS.DataManager], "contacts", response);
        logger.info(
            [LOGS.DataManager],
            `${response.length} Contacts found for search : ${searchString}`,
        );

        return response;
    }

    /** Comme `searchContact`, chaque contact avec son nom affiché (recherche d'un nom par l'assistant). */
    public async searchContactPeople(
        searchString: string,
        top: number = 20,
    ): Promise<ContactPerson[]> {
        try {
            return await this.dataApi.searchContactPeople(searchString, top);
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error in searchContactPeople call to dataApi: ${error}`,
            );
            throw error;
        }
    }

    /**
     * Recherche des emails selon les critères définis dans le ViewFilter
     * ═══════════════════════════════════════════════════════════════════════════
     * @param viewFilter - Critères de recherche pour les emails (keywords, dates, utilisateurs)
     * @returns Liste d'internetMessageIds correspondant aux critères de recherche
     */
    public async searchInternetMessageIdsFromViewFilter(
        viewFilter: ViewFilter,
        limit: number | undefined = undefined,
    ): Promise<DataSearchResult[]> {
        // const startTime = performance.now();
        logger.info(
            [LOGS.DataManager],
            `searchMailsFromViewFilter with filter...`,
        );

        const ENABLE_NEW_POSTFILTER = true;

        let matchingSearchResults: DataSearchResult[] = [];
        try {
            matchingSearchResults =
                await this.dataApi.searchInternetMessageIdsFromViewFilter(
                    viewFilter,
                    limit,
                );
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error in searchInternetMessageIdsFromViewFilter call to dataApi: ${error}`,
            );
            throw error;
        }

        try {
            // POST FILTER sur les full words

            // If Filter contains Keywords
            if (ENABLE_NEW_POSTFILTER && viewFilter.keywords.length > 0) {
                const matchingSearchResultsInPostFilterOnKeywords: DataSearchResult[] =
                    [];
                const excludedSearchResultsInPostFilterOnKeywords: DataSearchResult[] =
                    [];

                for (const searchResult of matchingSearchResults) {
                    const searchResultIsMatching = isSearchResultMatchKeyWords(
                        searchResult,
                        viewFilter.keywords,
                    );

                    // At least 1 keyword match
                    if (
                        searchResultIsMatching
                    ) // Mail dests contains at least 1 keyword
                    {
                        matchingSearchResultsInPostFilterOnKeywords.push(
                            searchResult,
                        );
                    } else {
                        excludedSearchResultsInPostFilterOnKeywords.push(
                            searchResult,
                        );
                        logger.info(
                            LOGS.DataManager,
                            `  Mail excluded in Keyword POSTFILTER : ${searchResult.subject}`,
                        );
                    }
                }

                matchingSearchResults =
                    matchingSearchResultsInPostFilterOnKeywords;
            }

            // POST FILTER sur les destinataires

            // If Filter contains Recipients
            if (
                ENABLE_NEW_POSTFILTER &&
                viewFilter.identifiedRecipients != null &&
                viewFilter.identifiedRecipients.length > 0
            ) {
                const matchingSearchResultsInPostFilterOnDests: DataSearchResult[] =
                    [];
                const excludedSearchResultsInPostFilterOnDests: DataSearchResult[] =
                    [];

                for (const searchResult of matchingSearchResults) {
                    const destList = [
                        ...(searchResult.dest
                            ?.map((dest) => dest.email)
                            .filter((email) => email != null) ?? []),
                        ...(searchResult.cc
                            ?.map((cc) => cc.email)
                            .filter((email) => email != null) ?? []),
                        ...(searchResult.bcc
                            ?.map((bcc) => bcc.email)
                            .filter((email) => email != null) ?? []),
                    ];

                    // If no dest found in mail (not normal!), we keep mail : should be the case for Google until fully implemented
                    if (destList.length === 0) {
                        logger.warn(
                            [LOGS.DataManager, LOGS.TESTS],
                            "DataManager.searchMailsFromUpdatedViewFilter has no Dests to filter on !!! Keeping mail ...",
                        );
                        matchingSearchResultsInPostFilterOnDests.push(
                            searchResult,
                        );
                    } else {
                        // At least 1 Recipient in filter
                        if (
                            destList.some((dest) => {
                                // dest ne devrait plus être null/undefined grâce au filter, mais on reste défensif
                                if (!dest) return false;
                                return viewFilter.identifiedRecipients?.includes(
                                    dest.toLowerCase(),
                                );
                            })
                        ) {
                            matchingSearchResultsInPostFilterOnDests.push(
                                searchResult,
                            );
                        } else {
                            excludedSearchResultsInPostFilterOnDests.push(
                                searchResult,
                            );
                            logger.info(
                                LOGS.DataManager,
                                `  Mail excluded in Dests POSTFILTER : ${searchResult.subject}`,
                            );
                        }
                    }
                }

                matchingSearchResults =
                    matchingSearchResultsInPostFilterOnDests;
            }

            // Résultats complets, date comprise : c'est la clé d'ordre transmise à l'API.
            return matchingSearchResults;
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error in searchMailsFromViewFilter: ${error}`,
            );
            throw new Error(
                `Error in searchInternetMessageIdsFromViewFilter: ${error.message}`,
            );
        }
    }

    /**
     * Déplace un email vers un dossier spécifique
     * ═══════════════════════════════════════════════════════════
     *
     * @param mailId - ID de l'email à déplacer
     * @param destinationId - ID du dossier de destination (ex: 'deleteditems', 'inbox', 'drafts', etc.)
     * @returns Promise<any> - return response id of mail moved
     */
    public async moveMail(
        mailId: string,
        destinationId: string,
    ): Promise<string> {
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `Moving mail ${mailId} to ${destinationId}...`,
        );

        try {
            const movedMail: Message = await this.dataApi.moveMail(
                mailId,
                destinationId,
            );

            if (!movedMail.id) {
                throw "Unexpected null id on moved mail";
            }

            logger.info(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                `Mail successfully moved to ${destinationId}`,
            );

            return movedMail.id;
        } catch (error) {
            logger.error(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                `Error in moveMail: ${error}`,
            );
            // AB#1764 — propager une Error (au lieu d'une string) en préservant le status HTTP,
            // pour permettre la détection 404 en amont (`isNotFoundError` → suppression idempotente).
            throw inheritHttpStatus(
                new Error(`Error in moveMail: ${error}`),
                error,
            );
        }
    }

    public async getGeneralNavBarContent(
        mailFolders: string[],
    ): Promise<NavItem[]> {
        try {
            const result =
                await this.dataApi.getGeneralNavBarContent(mailFolders);
            return result;
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `DataManager - Error in getGeneralNavBarContent : ${error}`,
            );
            throw error;
        }
    }

    /**
     * Get all mail internetMessageIds corresponding to a conversation ID
     * ═══════════════════════════════════════════════════════
     *
     * @param conversationId - ID of the conversation to fetch mails from
     * @returns Promise with array of mail internetMessageIds
     */
    public async getMailThreadInternetMessageIds(
        conversationId: string,
    ): Promise<string[]> {
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `Getting mail by conversationId ...`,
        );

        let internetMessageIds: string[] = [];
        try {
            const response =
                await this.dataApi.getMailThreadInternetMessageIds(
                    conversationId,
                );
            logger.info(LOGS.DataManager, "MailThread content", response);

            // Graph renvoie tous les messages de la conversation, tous dossiers confondus.
            // Un mail self-sent (utilisateur en CCI) existe en double (Sent + Inbox) sous le
            // même internetMessageId : sans dédoublonnage, getMailThread mappe deux fois le
            // même record et le thread contient deux fois le même mail (clé React dupliquée).
            internetMessageIds = Array.from(
                new Set(
                    response?.map((mail: any) => mail.internetMessageId) ?? [],
                ),
            );
        } catch (error) {
            logger.error(
                [LOGS.DataManager],
                `Error in getMailThreadInternetMessageIds call to dataApi: ${error}`,
            );
            if (error.details) {
                logger.info(
                    [LOGS.DataManager],
                    "getMailThreadInternetMessageIdsError",
                    error.details,
                );
            }
            throw error;
        }

        return internetMessageIds;
    }

    // #endregion Mails

    // #region Agenda
    public async getUserAgenda(filter: AgendaFilter): Promise<any> {
        logger.info(LOGS.DataManager, `getUserAgenda`);

        try {
            const response = await this.dataApi.getUserAgenda(filter);

            logger.info(LOGS.DataManager, "UserAgenda", response);

            return response;
        } catch (error) {
            logger.error(LOGS.DataManager, `Error in getUserAgenda: ${error}`);
            if (error.details) {
                logger.info([LOGS.DataManager], "getUserAgenda", error.details);
            }
            throw error;
        }
    }

    public async getUserAgendaPage(
        filter: AgendaFilter,
        cursor?: string,
        options?: { includeBody?: boolean },
    ): Promise<AgendaEventsPage> {
        logger.info(LOGS.DataManager, `getUserAgendaPage`);

        try {
            return await this.dataApi.getUserAgendaPage(
                filter,
                cursor,
                options,
            );
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `Error in getUserAgendaPage: ${error}`,
            );
            if (error.details) {
                logger.info(
                    [LOGS.DataManager],
                    "getUserAgendaPage",
                    error.details,
                );
            }
            throw error;
        }
    }

    public async getAgendaItemBodies(
        ids: string[],
    ): Promise<Record<string, AgendaItemBody>> {
        logger.info(
            LOGS.DataManager,
            `getAgendaItemBodies(${ids?.length ?? 0})`,
        );

        try {
            return await this.dataApi.getAgendaItemBodies(ids);
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `Error in getAgendaItemBodies: ${error}`,
            );
            throw error;
        }
    }

    public async createAgendaItem(
        agendaItemData: CreateAgendaItemData,
    ): Promise<boolean> {
        // const startTime = performance.now();
        logger.info(
            LOGS.DataManager,
            `createAgendaItem(${agendaItemData.subject})`,
        );

        try {
            const created = await this.dataApi.createAgendaItem(agendaItemData);

            if (created) {
                logger.info(
                    LOGS.DataManager,
                    "Agenda Item created successfully",
                );
            }
            return created;
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `Error in createAgendaItem: ${error}`,
            );
            return false;
        }
    }

    /**
     * Met à jour un événement existant dans le calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à mettre à jour
     * @input {UpdateAgendaItemData} updateData - Données à modifier
     * @output {Promise<boolean>} Succès de la mise à jour
     */
    public async updateAgendaItem(
        itemId: string,
        updateData: UpdateAgendaItemData,
    ): Promise<boolean> {
        logger.info(LOGS.DataManager, `updateAgendaItem(${itemId})`);

        try {
            const updated = await this.dataApi.updateAgendaItem(
                itemId,
                updateData,
            );

            if (updated) {
                logger.info(
                    LOGS.DataManager,
                    "Agenda Item updated successfully",
                );
            }
            return updated;
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `Error in updateAgendaItem: ${error}`,
            );
            return false;
        }
    }

    /**
     * Supprime un événement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à supprimer
     * @output {Promise<boolean>} Succès de la suppression
     */
    public async deleteAgendaItem(itemId: string): Promise<boolean> {
        logger.info(LOGS.DataManager, `deleteAgendaItem(${itemId})`);

        try {
            const deleted = await this.dataApi.deleteAgendaItem(itemId);

            if (deleted) {
                logger.info(
                    LOGS.DataManager,
                    "Agenda Item deleted successfully",
                );
            }
            return deleted;
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `Error in deleteAgendaItem: ${error}`,
            );
            return false;
        }
    }

    /**
     * Accepte un évènement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à accepter
     * @outpur {Promise<boolean>} Succès de l'acceptation
     */
    public async acceptAgendaItem(itemId: string): Promise<boolean> {
        logger.info(LOGS.DataManager, `acceptAgendaItem(${itemId})`);

        try {
            await this.dataApi.acceptAgendaItem(itemId);

            logger.info(LOGS.DataManager, "Agenda Item accepted successfully");
            return true;
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `Error in acceptAgendaItem: ${error}`,
            );
            return false;
        }
    }

    /**
     * Accepte provisoirement un évènement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à accepter provisoirement
     * @outpur {Promise<boolean>} Succès de l'acceptation provisoire
     */
    public async tentativelyAcceptAgendaItem(itemId: string): Promise<boolean> {
        logger.info(LOGS.DataManager, `tentativelyAcceptAgendaItem(${itemId})`);

        try {
            await this.dataApi.tentativelyAcceptAgendaItem(itemId);

            logger.info(
                LOGS.DataManager,
                "Agenda Item accepted tentatively successfully",
            );
            return true;
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `Error in tentativelyAcceptAgendaItem: ${error}`,
            );
            return false;
        }
    }

    /**
     * Refuse un évènement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à accepter
     * @outpur {Promise<boolean>} Succès de l'acceptation
     */
    public async declineAgendaItem(itemId: string): Promise<boolean> {
        logger.info(LOGS.DataManager, `declineAgendaItem(${itemId})`);

        try {
            await this.dataApi.declineAgendaItem(itemId);

            logger.info(LOGS.DataManager, "Agenda Item declined successfully");
            return true;
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `Error in declineAgendaItem: ${error}`,
            );
            return false;
        }
    }

    /**
     * Récupère les événements d'agenda modifiés depuis une date donnée
     * ═══════════════════════════════════════════════════════════════════════════
     * @param sinceDate - Date de début pour la recherche (format ISO)
     * @returns Promise<any[]> - Liste des événements modifiés depuis cette date
     */
    public async getUserAgendaModifiedSince(sinceDate: string): Promise<any[]> {
        logger.info(
            [LOGS.DataManager],
            `getUserAgendaModifiedSince(${sinceDate})`,
        );

        try {
            const response =
                await this.dataApi.getUserAgendaModifiedSince(sinceDate);

            logger.info(
                [LOGS.DataManager],
                `Retrieved ${response.length} agenda events modified since ${sinceDate}`,
            );
            logger.info([LOGS.DataManager], "Modified Agenda Events", response);

            return response;
        } catch (error) {
            logger.error(
                LOGS.DataManager,
                `Error in getUserAgendaModifiedSince: ${error}`,
            );
            if (error.details) {
                logger.info(
                    [LOGS.DataManager],
                    "getUserAgendaModifiedSince",
                    error.details,
                );
            }
            throw error;
        }
    }

    // #endregion Agenda

    // #region Documents

    public async getRecentDocuments(
        numberOfDocuments: number = 20,
    ): Promise<DriveItem[]> {
        // const startTime = performance.now();
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `getRecentDocuments (${numberOfDocuments}) ... `,
        );

        let documents: DriveItem[] = [];
        try {
            documents =
                await this.dataApi.getRecentDocuments(numberOfDocuments);
        } catch (error) {
            logger.error(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                `Error in getRecentDocuments : ${error}`,
            );
            throw new Error(`Error in getRecentDocuments: ${error.message}`);
        }

        return documents;
    }

    // #endregion Documents

    // #region Divers

    /**
     * Effectue une requête GET générique à l'API Graph
     * ═══════════════════════════════════════════════════════════
     *
     * @param requestUrl - URL relative de la requête (sans le préfixe de l'URL de l'API Graph)
     * @returns Promise avec le résultat JSON de la requête
     */
    public async getGraphData(requestUrl: string): Promise<any> {
        // const startTime = performance.now();
        logger.info(
            [LOGS.DataManager, LOGS.AnalysisLifecycle],
            `getGraphData(${requestUrl})`,
        );

        try {
            const response = await this.dataApi.getGraphData(requestUrl);
            return response;
        } catch (error) {
            logger.error(
                [LOGS.DataManager, LOGS.AnalysisLifecycle],
                `Error in getGraphData: ${error}`,
            );
            throw error;
        }
    }

    /**
     * Génère une URL d'image contenant les initiales de l'utilisateur
     * @param displayName Nom complet de l'utilisateur
     * @returns URL de l'image générée
     */
    public generateInitialsPhoto(displayName: string): string {
        const initials = displayName
            .split(" ")
            .map((name) => name.charAt(0))
            .join("")
            .toUpperCase()
            .substring(0, 2);

        let hash = 0;
        for (let i = 0; i < displayName.length; i++) {
            hash = displayName.charCodeAt(i) + ((hash << 5) - hash);
        }

        let color = "#";
        for (let i = 0; i < 3; i++) {
            const value = (hash >> (i * 8)) & 0xff;
            color += ("00" + value.toString(16)).substr(-2);
        }

        const svg = `
                    <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="50" cy="50" r="50" fill="${color}"/>
                        <text x="50" y="50" text-anchor="middle" dominant-baseline="middle" 
                            fill="white" font-family="Arial" font-size="40" font-weight="bold">
                            ${initials}
                        </text>
                    </svg>
                `;

        return `data:image/svg+xml;base64,${btoa(svg)}`;
    }

    // #endregion Divers

    // #endregion Public Methods

    // #region Private Methods

    /**
     * Process inline images in email content
     * Converts data URI images to CID attachments for better email client compatibility
     * ═══════════════════════════════════════════════════════════════════════════
     *
     * @param mailData - Original mail data with potential data URI images
     * @returns Modified mail data with CID references and inline attachments
     */
    private _processInlineImages(
        mailData: MailSend | ReplyForward,
    ): MailSend | ReplyForward {
        logger.info(
            [LOGS.DataManager],
            "Processing inline images in content...",
        );

        // Create a copy to avoid mutating the original
        const processedData = { ...mailData };
        let htmlContent = processedData.content;

        // Skip if no content or not HTML
        if (!htmlContent || processedData.contentType !== "HTML") {
            return processedData;
        }

        // Initialize attachments array if not present
        if (!processedData.attachments) {
            processedData.attachments = [];
        }
        // Regular expression to find data URI images
        const dataUriRegex = /src="data:([^;]+);base64,([^"]+)"/g;
        const matches: Array<{
            fullMatch: string;
            mimeType: string;
            base64Data: string;
        }> = [];
        let match;

        // Collect all matches first (to avoid regex state issues)
        while ((match = dataUriRegex.exec(htmlContent)) !== null) {
            matches.push({
                fullMatch: match[0],
                mimeType: match[1],
                base64Data: match[2],
            });
        }

        // Process each match
        matches.forEach((matchData, index) => {
            const { mimeType, base64Data } = matchData;

            // Generate unique Content-ID — UUID prevents collisions across composed emails in a thread
            const contentId = `${generateUUID()}@mailvista`;

            // Determine file extension from MIME type
            const extension = mimeType.split("/")[1] || "png";
            const fileName = `inline-image-${index}.${extension}`;
            logger.info(
                [LOGS.DataManager],
                `Found inline image: ${fileName} (${mimeType})`,
            );

            // Create inline attachment
            const inlineAttachment: Attachment = {
                name: fileName,
                contentType: mimeType,
                contentBytes: base64Data,
                isInline: true,
                contentId: contentId,
            };
            processedData.attachments!.push(inlineAttachment);

            // Replace data URI with CID reference
            const cidReference = `src="cid:${contentId}"`;
            htmlContent = htmlContent.replace(
                matchData.fullMatch,
                cidReference,
            );
        });

        // Update processed data
        processedData.content = htmlContent;
        if (matches.length > 0) {
            logger.info(
                [LOGS.DataManager],
                `Processed ${matches.length} inline images`,
            );
        }

        return processedData;
    }

    // #endregion Private Methods
}
